import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { createLead } from "@/lib/api";

const SERVICES = [
  { value: "consulenza_aziendale", label: "Consulenza Aziendale" },
  { value: "business_plan", label: "Business Plan su Misura" },
  { value: "finanza_agevolata", label: "Finanza Agevolata" },
  { value: "pianificazione_strategica", label: "Pianificazione Strategica" },
  { value: "non_so_ancora", label: "Non so ancora" },
];

const initial = {
  company: "",
  company_year: "",
  vat_number: "",
  name: "",
  phone: "",
  email: "",
  service: "non_so_ancora",
  message: "",
  privacy_accepted: false,
};

const STEPS = [
  { label: "Azienda" },
  { label: "Contatti" },
  { label: "Richiesta" },
];

export default function ContactForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validateStep = (s) => {
    const er = {};
    if (s === 0) {
      if (form.company_year && !/^(19|20|21)\d{2}$/.test(form.company_year))
        er.company_year = "Inserisci un anno valido (es. 2018).";
      if (form.vat_number && form.vat_number.replace(/\s/g, "").length < 8)
        er.vat_number = "Partita IVA non valida.";
    } else if (s === 1) {
      if (form.phone.trim().length < 6) er.phone = "Inserisci un numero di cellulare valido.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Email non valida.";
    } else if (s === 2) {
      if (!form.privacy_accepted) er.privacy_accepted = "Per inviare devi accettare la Privacy Policy.";
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const onBack = () => setStep((s) => Math.max(s - 1, 0));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(2)) return;
    setLoading(true);
    try {
      await createLead({
        name: form.name.trim() || "Non specificato",
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        company_year: form.company_year ? parseInt(form.company_year, 10) : null,
        vat_number: form.vat_number.trim() || null,
        service: form.service,
        message: form.message.trim() || null,
        privacy_accepted: true,
      });
      setDone(true);
      setForm(initial);
      setStep(0);
      toast.success("Richiesta inviata. Ti ricontattiamo entro 24 ore lavorative.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore nell'invio. Riprova tra qualche minuto.";
      toast.error(typeof detail === "string" ? detail : "Errore nell'invio.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-form-success" className="border border-[#0E2E4D]/15 bg-white p-10" id="form">
        <p className="overline">Richiesta ricevuta</p>
        <h3 className="h-display text-3xl md:text-4xl mt-4">
          Grazie. Ti ricontattiamo entro 24 ore lavorative.
        </h3>
        <p className="mt-4 text-[15px] text-[#0E2E4D]/70 max-w-lg">
          Un partner di BV analizzerà la tua richiesta e ti scriverà personalmente per fissare una prima call
          conoscitiva.
        </p>
        <button
          data-testid="contact-form-reset"
          onClick={() => setDone(false)}
          className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-mono link-underline"
        >
          Invia un'altra richiesta <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <form
      id="form"
      data-testid="contact-form"
      onSubmit={onSubmit}
      className="border border-[#0E2E4D]/15 bg-white scroll-mt-28"
      noValidate
    >
      <div className="px-8 pt-8 pb-2">
        <h3 className="h-display text-2xl md:text-3xl text-[#0E2E4D]" data-testid="contact-form-title">
          Entra in contatto con noi
        </h3>
        <p className="mt-3 text-[14px] text-[#0E2E4D]/60">
          Compila il form: ti rispondiamo entro 24 ore lavorative.
        </p>
      </div>

      {/* Progress bar */}
      <div className="px-8 mt-6" data-testid="contact-form-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#0E2E4D]/55">
            Step {step + 1} / {STEPS.length} · {STEPS[step].label}
          </span>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#2BA8AD]">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="h-1.5 w-full bg-[#0E2E4D]/10 overflow-hidden">
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%`, backgroundColor: "#2BA8AD" }}
            data-testid="contact-form-progress-bar"
          />
        </div>
      </div>

      {/* STEP 1 — Azienda */}
      {step === 0 && (
        <div className="px-8 mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5" data-testid="contact-form-step-1">
          <Field label="Nome Azienda" name="company" error={errors.company} className="md:col-span-2">
            <input data-testid="contact-input-company" name="company" value={form.company} onChange={onChange} placeholder="Es. Rossi S.r.l." className="bv-input" autoComplete="organization" />
          </Field>
          <Field label="Anno di Costituzione" name="company_year" error={errors.company_year}>
            <input data-testid="contact-input-company-year" name="company_year" value={form.company_year} onChange={onChange} inputMode="numeric" maxLength={4} placeholder="2018" className="bv-input" />
          </Field>
          <Field label="Partita IVA" name="vat_number" error={errors.vat_number}>
            <input data-testid="contact-input-vat" name="vat_number" value={form.vat_number} onChange={onChange} placeholder="IT01234567890" className="bv-input" />
          </Field>
        </div>
      )}

      {/* STEP 2 — Contatti */}
      {step === 1 && (
        <div className="px-8 mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5" data-testid="contact-form-step-2">
          <Field label="Nome Amministratore" name="name" error={errors.name} className="md:col-span-2">
            <input data-testid="contact-input-name" name="name" value={form.name} onChange={onChange} placeholder="Mario Rossi" className="bv-input" autoComplete="name" />
          </Field>
          <Field label="Numero di Cellulare *" name="phone" error={errors.phone}>
            <input data-testid="contact-input-phone" name="phone" value={form.phone} onChange={onChange} placeholder="+39 333 0000000" className="bv-input" autoComplete="tel" />
          </Field>
          <Field label="E-Mail *" name="email" error={errors.email}>
            <input data-testid="contact-input-email" name="email" type="email" value={form.email} onChange={onChange} placeholder="mario.rossi@azienda.it" className="bv-input" autoComplete="email" />
          </Field>
        </div>
      )}

      {/* STEP 3 — Richiesta */}
      {step === 2 && (
        <div className="px-8 mt-8 grid grid-cols-1 gap-y-5" data-testid="contact-form-step-3">
          <Field label="Di cosa hai bisogno?" name="service">
            <select data-testid="contact-input-service" name="service" value={form.service} onChange={onChange} className="bv-input bg-transparent">
              {SERVICES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Messaggio" name="message">
            <textarea
              data-testid="contact-input-message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={5}
              placeholder="Settore, fatturato indicativo, principale ostacolo alla crescita, obiettivo nei prossimi 12 mesi…"
              className="bv-input resize-none"
            />
          </Field>

          <label className="flex items-start gap-3 cursor-pointer select-none pt-2">
            <input
              data-testid="contact-input-privacy"
              type="checkbox"
              name="privacy_accepted"
              checked={form.privacy_accepted}
              onChange={onChange}
              className="mt-1 accent-[#0E2E4D] w-4 h-4"
            />
            <span className="text-[13px] text-[#0E2E4D]/75 leading-relaxed">
              Ho letto e accetto la <a href="#" className="underline">Privacy Policy</a> e il trattamento dei
              miei dati personali ai sensi del GDPR.
            </span>
          </label>
          {errors.privacy_accepted && (
            <span data-testid="error-privacy" className="text-[12px] text-red-700 font-mono">
              {errors.privacy_accepted}
            </span>
          )}
        </div>
      )}

      {/* Navigation buttons */}
      <div className="px-8 pt-8 pb-8 mt-2 flex items-center justify-between gap-4">
        {step > 0 ? (
          <button
            type="button"
            onClick={onBack}
            data-testid="contact-form-back"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.20em] uppercase font-display font-bold text-[#0E2E4D]/70 hover:text-[#0E2E4D] transition-colors"
          >
            <ArrowLeft size={14} /> Torna indietro
          </button>
        ) : <span aria-hidden />}

        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={onNext}
            data-testid="contact-form-next"
            className="inline-flex items-center justify-center gap-2 bg-[#0E2E4D] text-white px-7 py-4 text-[12px] tracking-[0.20em] uppercase font-display font-bold hover:bg-[#2BA8AD] transition-colors"
          >
            Procedi <ArrowRight size={14} />
          </button>
        ) : (
          <button
            data-testid="contact-form-submit"
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-[#0E2E4D] text-white px-7 py-4 text-[12px] tracking-[0.20em] uppercase font-display font-bold hover:bg-[#2BA8AD] transition-colors disabled:opacity-60"
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Invio…</>
            ) : (
              <>Invia <Check size={14} /></>
            )}
          </button>
        )}
      </div>

      <style>{`
        .bv-input {
          width: 100%;
          background: #F5F2EB;
          padding: 16px 18px;
          border: 1px solid rgba(14,46,77,0.12);
          outline: none;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 15px;
          color: #0E2E4D;
          border-radius: 2px;
          transition: border-color .2s ease, background-color .2s ease;
        }
        .bv-input::placeholder { color: rgba(14,46,77,0.4); }
        .bv-input:focus { border-color: #2BA8AD; background: #fff; }
        .bv-field-label {
          display:block;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(14,46,77,0.7);
          margin-bottom: 6px;
        }
        .bv-field-error {
          display:block;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: #b91c1c;
          margin-top: 6px;
        }
      `}</style>
    </form>
  );
}

function Field({ label, name, error, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="bv-field-label">{label}</span>
      {children}
      {error && <span className="bv-field-error" data-testid={`error-${name}`}>{error}</span>}
    </label>
  );
}
